import React, { useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Store, 
  Book, 
  Users, 
  Package, 
  DollarSign, 
  Award,
  ShoppingCart,
  TrendingUp,
  Building2,
  UserCheck,
  Megaphone,
  Truck,
  ClipboardCheck,
  ClipboardList
} from 'lucide-react';
import styled from 'styled-components';
import { ThemeColors, Spacing, Typography } from '../../styles/ThemeStyles';
import { PageContainer, Grid } from '../../styles/StyledComponents';
import { DashboardCard } from '../../components/shared/DashboardCard';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { AnnouncementCard } from '../../components/announcements/AnnouncementCard';

const MainContent = styled.main`
  padding: ${Spacing.lg} ${Spacing.md};
  padding-top: calc(${Spacing.md} + 10px); /* Account for fixed header */
`;

const WelcomeHeading = styled.h1`
  font-family: 'Roboto', sans-serif;
  font-size: 2rem;
  font-weight: 500;
  color: ${ThemeColors.secondary.main};
  text-align: left;
  margin-bottom: ${Spacing.xl};
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing.md};
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
  margin-top: ${Spacing.xl};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const QuickActionButton = styled.button`
  background: white;
  border: 2px solid ${ThemeColors.border.light};
  border-radius: 12px;
  padding: ${Spacing.sm} ${Spacing.md};
  cursor: pointer;
  transition: all 0.3s ease;
  color: ${ThemeColors.primary.main};
  font-weight: ${Typography.fontWeight.medium};
  font-size: ${Typography.fontSize.sm};
  width: 100%;
  margin-top: ${Spacing.md};

  &:hover {
    border-color: ${ThemeColors.primary.main};
    transform: translateY(-2px);
  }
`;

export const ManagerDashboardScreenMobile = React.memo(() => {
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { 
    announcements, 
    loading: isLoadingAnnouncements 
  } = useAnnouncements({ status: 'ACTIVE' });

  const menuItems = useMemo(() => {
    const isHandLead = userRole === 'hand-lead';
    
    const items = [
      // Main Section
      {
        id: 'orders',
        label: 'Order Management',
        icon: ShoppingCart,
        description: 'View, approve, and manage customer orders. Track order status and history.',
        action: () => navigate('/manager/orders')
      },
      {
        id: 'products',
        label: 'Product Management',
        icon: Package,
        description: 'Control product availability. Mark items as out-of-stock to remove them from sales rep orders.',
        action: () => navigate('/manager/products')
      },
      {
        id: 'stores',
        label: 'Store Management',
        icon: Building2,
        description: 'Manage store information, assignments, and customer accounts.',
        action: () => navigate('/manager/stores')
      },
      {
        id: 'analytics',
        label: t('navigation.analytics', 'Analytics & Performance'),
        icon: TrendingUp,
        description: t('dashboard.analyticsDesc', 'View activity logs, performance metrics, and team analytics.'),
        action: () => navigate('/manager/analytics')
      },
    ];
    
    // User Management - only for managers, not hand-lead
    if (!isHandLead) {
      items.push({
        id: 'user-management',
        label: t('navigation.userManagement', 'User Management'),
        icon: UserCheck,
        description: t('dashboard.userManagementDesc', 'Manage users, roles, and permissions. Create and edit user accounts.'),
        action: () => navigate('/manager/users')
      });
    }
    
    // Resource Library - different access for hand-lead
    if (isHandLead) {
      items.push({
        id: 'resources',
        label: t('navigation.resources', 'Resource Library'),
        icon: Book,
        description: t('dashboard.resourcesDesc', 'Access training materials, product guides, videos, and documents.'),
        action: () => navigate('/resources')
      });
    } else {
      items.push({
        id: 'resource-management',
        label: t('navigation.resourceManagement', 'Resource Library'),
        icon: Book,
        description: t('dashboard.resourceManagementDesc', 'Create and manage training materials, guides, documents, and videos.'),
        action: () => navigate('/manager/resources')
      });
    }
    
    items.push({
      id: 'store-visit',
      label: t('navigation.storeVisit', 'Store Visit'),
      icon: Store,
      description: t('dashboard.storeVisitDesc', 'Visit stores, manage information, and track customer interactions.'),
      action: () => navigate('/manager/store-visit')
    });
    
    // Shipments - different access for hand-lead
    if (isHandLead) {
      items.push({
        id: 'shipments',
        label: 'Shipments',
        icon: Truck,
        description: 'View shipment notifications and delivery schedules for your sales group.',
        action: () => navigate('/shipments')
      });
    } else {
      items.push({
        id: 'shipments',
        label: 'Shipments',
        icon: Truck,
        description: 'Track and manage shipment notifications, delivery schedules, and logistics.',
        action: () => navigate('/manager/shipments')
      });
    }
    
    // Tools Section
    items.push(
      {
        id: 'payroll',
        label: t('navigation.payroll', 'Payroll'),
        icon: DollarSign,
        description: t('dashboard.payrollDesc', 'View commission reports, payment history, and expense tracking.'),
        action: () => navigate('/manager/payroll')
      },
      {
        id: 'contest-entry',
        label: t('navigation.contestEntry', 'Contest Entry'),
        icon: Award,
        description: t('dashboard.contestEntryDesc', 'Participate in sales contests and view performance rankings.'),
        action: () => navigate('/manager/contest-entry')
      },
    );

    if (isHandLead) {
      items.push({
        id: 'eos-survey',
        label: 'EOS Survey',
        icon: ClipboardList,
        description: 'Complete your End-of-Season survey.',
        action: () => navigate('/manager/eos-survey')
      });
    }

    items.push(
      {
        id: 'tradeshow',
        label: 'Trade Show Orders',
        icon: ClipboardCheck,
        description: 'Review display orders submitted from the public trade show form.',
        action: () => navigate('/manager/tradeshow')
      },
      {
        id: 'announcements',
        label: 'Announcements',
        icon: Megaphone,
        description: 'Create and manage announcements for sales representatives and teams.',
        action: () => navigate('/manager/announcements')
      }
    );
    
    return items;
  }, [t, navigate, userRole]);

  return (
    <PageContainer isMenuCollapsed={true}>
      <MainContent>
        <WelcomeHeading>
          {t('dashboard.welcomeBack', 'Welcome Back')} {user?.firstName || user?.username || ''}
        </WelcomeHeading>
        
        {/* Main Navigation Cards */}
        <SectionTitle>Management Tools</SectionTitle>
        <CardGrid>
          {menuItems.map((item) => (
            <DashboardCard
              key={item.id}
              id={item.id}
              label={item.label}
              description={item.description}
              icon={item.icon}
              onClick={item.action}
              variant="mobile"
            />
          ))}
        </CardGrid>

        {/* Announcements Section */}
        {announcements.length > 0 && (
          <>
            <SectionTitle>
              <Megaphone style={{ display: 'inline', verticalAlign: 'middle' }} size={18} />
              Recent Announcements
            </SectionTitle>
            <Grid minWidth="280px" gap={Spacing.md}>
              {announcements.slice(0, 3).map((announcement) => (
                <AnnouncementCard
                  key={announcement.id}
                  announcement={announcement}
                  variant="mobile"
                  onView={(id) => navigate(`/manager/announcements/${id}`)}
                />
              ))}
            </Grid>
            {announcements.length > 3 && (
              <QuickActionButton onClick={() => navigate('/manager/announcements')}>
                View All Announcements ({announcements.length})
              </QuickActionButton>
            )}
          </>
        )}
      </MainContent>
    </PageContainer>
  );
});

ManagerDashboardScreenMobile.displayName = 'ManagerDashboardScreenMobile';